/**
 * ScorecardsAdmin.tsx — Admin → Account → Scorecards
 *
 * Lists conversation scorecard templates with an active toggle and add/edit/delete.
 * Editing opens the full ScorecardEditorPage (/admin/scorecards/:id).
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): no Add, Edit or Delete, and the
 * active switch shows its state without toggling (an AdminLockedFieldset). A
 * row click still opens the scorecard, which the editor shows read-only.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { Spinner } from '@/components/ui/Spinner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/store/toastStore';
import * as scorecardsSvc from '@/services/scorecards.service';
import type { ScorecardTemplateListItem } from '@/services/scorecards.service';

export function ScorecardsAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locked = useAdminPageLevel() === 'locked';
  const [deleteTarget, setDeleteTarget] = useState<ScorecardTemplateListItem | null>(null);

  const { data: scorecards, isLoading } = useQuery({
    queryKey: ['admin-scorecards'],
    queryFn: scorecardsSvc.listScorecards,
    staleTime: 30_000,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      scorecardsSvc.toggleScorecardActive(id, active),
    onMutate: async ({ id, active }) => {
      await queryClient.cancelQueries({ queryKey: ['admin-scorecards'] });
      const prev = queryClient.getQueryData<ScorecardTemplateListItem[]>(['admin-scorecards']);
      queryClient.setQueryData<ScorecardTemplateListItem[]>(['admin-scorecards'], (list) =>
        (list ?? []).map((s) => (s.scorecardTemplateID === id ? { ...s, active } : s)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['admin-scorecards'], ctx.prev);
      toast.error('Failed to update scorecard');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['admin-scorecards'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => scorecardsSvc.deleteScorecard(id),
    onSuccess: () => {
      toast.success('Scorecard deleted');
      queryClient.invalidateQueries({ queryKey: ['admin-scorecards'] });
    },
    onError: () => toast.error('Failed to delete scorecard'),
    onSettled: () => setDeleteTarget(null),
  });

  const list = scorecards ?? [];
  const activeCount = list.filter((s) => s.active).length;

  return (
    <div className="p-6">
      <PageHeader
        title="Scorecard Templates"
        actions={
          locked ? undefined : (
            <Button onClick={() => navigate('/admin/scorecards/new')}>
              <Plus className="mr-1 h-4 w-4" />
              Add scorecard
            </Button>
          )
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
            Active scorecards
            <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-slate-100 px-1.5 text-xs font-bold text-slate-600">
              {activeCount}
            </span>
          </div>

          {list.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 py-16 text-center">
              <p className="text-sm text-slate-500">No scorecards yet.</p>
              {!locked && (
                <Button className="mt-3" onClick={() => navigate('/admin/scorecards/new')}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add scorecard
                </Button>
              )}
            </div>
          ) : (
            <AdminLockedFieldset>
              <div className="space-y-2">
                {list.map((s) => (
                  <div
                    key={s.scorecardTemplateID}
                    onClick={() => navigate(`/admin/scorecards/${s.scorecardTemplateID}`)}
                    className="flex cursor-pointer items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 transition hover:border-indigo-200 hover:shadow-sm"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{s.name}</p>
                      <p className="text-xs text-slate-500">
                        All Teams · {s.fieldCount} field{s.fieldCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={s.active}
                        onChange={(active) =>
                          toggleMutation.mutate({ id: s.scorecardTemplateID, active })
                        }
                        size="md"
                      />
                      {!locked && (
                        <>
                          <button
                            type="button"
                            title="Edit"
                            onClick={() => navigate(`/admin/scorecards/${s.scorecardTemplateID}`)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={() => setDeleteTarget(s)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </AdminLockedFieldset>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Delete Scorecard"
        message={deleteTarget ? `Delete "${deleteTarget.name}"? This cannot be undone.` : ''}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => { if (deleteTarget) deleteMutation.mutate(deleteTarget.scorecardTemplateID); }}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

export default ScorecardsAdmin;
